import {
  CRField,
  CRWarning,
  FieldDefn,
  FieldDefnLength,
  FieldDefnNumeric,
  FieldDefnOverride,
  FieldDefnText,
  FieldType,
  ValidationResult,
} from '@smart-builder/types';
import { isNullOrEmptyStr, parseLength, stripFieldName } from './helpers';
import { _replaceTokens } from './translations';

export const SBMountedFields: Record<string, number> = {};

// we use the `fieldType` to check if something is a CRField as it is nonnullable and should always be there in any CRField
export function isCRField<T>(field?: T | CRField<T>): field is CRField<T> {
  return (field as CRField<T>)?.fieldType !== undefined ?? false;
}

export function filledInFieldDefn<T extends FieldDefn>(defn: T) {
  return {
    ...defn,
    required: defn.required ?? false,
    readOnly: defn.readOnly ?? false,
    options: defn.options ?? [],
    resetSubsequentQuestionsOnNewValue:
      defn.resetSubsequentQuestionsOnNewValue ?? false,
    spinStep: [
      FieldType.integer,
      FieldType.float,
      FieldType.percent,
      FieldType.currency,
    ].includes(defn.fieldType)
      ? (defn as FieldDefnNumeric).spinStep ?? 1
      : undefined,
    spinDirection: [
      FieldType.integer,
      FieldType.float,
      FieldType.percent,
      FieldType.currency,
    ].includes(defn.fieldType)
      ? (defn as FieldDefnNumeric).spinDirection ?? 'vertical'
      : undefined,
  };
}

export function doApplyFieldOverride(
  field: CRField,
  override?: FieldDefnOverride,
): CRField {
  if (override) {
    const { fieldName, fieldType } = field;
    const ovEntry = {
      ...override['*'],
      ...override[fieldType],
      ...override[fieldName ? stripFieldName(fieldName) : 0],
    };

    return {
      ...field,
      ...ovEntry,
    };
  }
  return field;
}

export function applyFieldOverride(
  field?: CRField,
  overrides?: FieldDefnOverride[],
): CRField | undefined {
  let result = field;
  if (result && overrides) {
    overrides.forEach(override => {
      result = doApplyFieldOverride(result!, override);
    });
  }
  return result;
}

export const validate = (
  value: any,
  fieldDefn: FieldDefn,
  warning?: CRWarning,
): ValidationResult | undefined => {
  // The following are handled by the control rather then validation
  // maxLength
  // integer
  // minValue
  // maxValue
  const { title, fieldType, required } = filledInFieldDefn(fieldDefn);
  const isImperialLengthField =
    SB_CONFIG.isImperial &&
    [FieldType.length, FieldType.lengthF].includes(fieldType);

  if (
    // Allow imperial length fields to be null
    !isImperialLengthField &&
    required &&
    (value === undefined || value === null)
  ) {
    return {
      message: _replaceTokens('%1% is required.', [title]),
      action: 'warn',
    };
  }

  if (
    value &&
    [FieldType.text, FieldType.email, FieldType.phone, FieldType.memo].includes(
      fieldType,
    )
  ) {
    const { regex, minLength } = fieldDefn as FieldDefnText;

    if (minLength && value?.length < minLength) {
      return {
        message: _replaceTokens('%1% must have at least %2% characters', [
          title,
          '' + minLength,
        ]),
        action: 'warn',
      };
    }
    if (regex || fieldType === FieldType.email || FieldType.phone) {
      const regexCheck = new RegExp(
        regex
          ? regex
          : fieldType === FieldType.email
          ? /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
          : fieldType === FieldType.phone
          ? // This should be defined somewhere.
            /^([0-9()-. ]{7,}$)/i
          : '',
      );
      if (!regexCheck.test(value)) {
        return {
          message: _replaceTokens('%1% is not valid.', [title]),
          action: 'warn',
        };
      }
    }
  }

  if (isImperialLengthField && !isNullOrEmptyStr(value)) {
    const { allowNegative } = fieldDefn as FieldDefnLength;

    const trimmedValue = ('' + value).trim();
    let lengthParseError: string | undefined;
    const lengthValue = parseLength(
      trimmedValue,
      fieldType === FieldType.lengthF,
      error => (lengthParseError = error),
    );
    if (isNaN(lengthValue)) {
      return { message: lengthParseError!, action: 'trap-focus' };
    } else if (!allowNegative && lengthValue < 0) {
      return {
        message: _replaceTokens('%1% must be positive.', [title]),
        action: 'trap-focus',
      };
    }
  }

  if (warning?.text && warning?.showOnField) {
    return { message: warning.text, action: 'warn' };
  }

  return undefined;
};
// Should only be called from the outside on the root CR.
export const mergeCRFields = (
  lastBody: any,
  currentBody: any,
  overrides?: FieldDefnOverride[],
): any => {
  // Either it's there, or it isn't.
  const warnings = currentBody.warnings as CRWarning[];

  const onField = (field: CRField, lastField: CRField): CRField => {
    const overridenField = applyFieldOverride(field, overrides)!;

    let resultField;
    if (
      lastField &&
      lastField.fieldName === overridenField.fieldName &&
      (overridenField.server.sequenceNum ?? 1) <
        (lastField.client.sequenceNum ?? 1)
    ) {
      // Keep existing client values, as the client values are more recent.
      resultField = {
        ...lastField,
        // Keep the field defn the same. Only update the server values.
        // For compound fields, also leave the sub-fields alone.
        server: field.server,
      };
    } else {
      // (Re)Initialising the client values from the server
      if (overridenField.fieldType === FieldType.compound) {
        // Relationship between a compound field and it's child fields is as follows:
        //   - sequenceNum is only sent from the server to the client for the compound field. Any sequence num sent
        //       on the subFields are ignored and replaced.
        //   - value is only sent on the subFields from the server to the client. The compound value is built
        //       below dynamically from it's subFields.
        // The client should never dispatch an update against the compound field directly. All updates should be
        //   called against the subfields.
        const compoundField = overridenField;
        const compoundFieldSequenceNum = compoundField.server.sequenceNum;
        const compoundValue = {} as Record<string, any>;
        const subFields = compoundField.fields.map(subField => {
          compoundValue[stripFieldName(subField.fieldName)] =
            subField.server.value;
          return {
            ...subField,
            client: {
              value: subField.server.value,
              sequenceNum: compoundFieldSequenceNum,
              validationResult: validate(
                subField.server.value,
                subField,
                warnings?.find(
                  warning => warning.fieldName === subField.fieldName,
                ),
              ),
            },
            server: {
              ...subField.server,
              sequenceNum: compoundFieldSequenceNum,
            },
          };
        });

        resultField = {
          ...compoundField,
          client: {
            value: compoundValue,
            sequenceNum: compoundFieldSequenceNum,
            validationResult: subFields.find(
              subField => subField.client.validationResult,
            )?.client.validationResult,
          },
          fields: subFields,
        };
      } else {
        const validationResult = validate(
          overridenField.server.value,
          overridenField,
          warnings?.find(
            warning => warning.fieldName === overridenField.fieldName,
          ),
        );

        let value = overridenField.server.value;
        if (
          [FieldType.length, FieldType.lengthF].includes(
            overridenField.fieldType,
          ) &&
          typeof value === 'string'
        ) {
          if (SB_CONFIG.isImperial && value.endsWith('"')) {
            value = value.substr(0, value.length - 1);
          } else if (!SB_CONFIG.isImperial) {
            if (value.endsWith('mm')) {
              value = value.substr(0, value.length - 2);
            }
            if (isNullOrEmptyStr(value)) {
              value = undefined;
            } else {
              value = parseFloat(value);
            }
          }
        }

        resultField = {
          ...overridenField,
          client: {
            value, //:  overridenField.server.value,
            sequenceNum: overridenField.server.sequenceNum ?? 1,
            validationResult: validationResult,
          },
        };
      }
    }
    return resultField;
  };
  return recurseBuildCRFields(currentBody, lastBody, onField);
};
export const recurseBuildCRFields = (
  currentBody: any,
  lastBody: any,
  onCRField: (currentField: CRField, lastField: CRField) => any,
  onCRObject?: (mergedObject: any, currentObject: any, lastObject: any) => any,
) => {
  if (isCRField(currentBody)) {
    // Is CRField
    const field = currentBody as CRField;
    const lastField = lastBody as CRField;
    return onCRField(field, lastField);
  } else if (typeof currentBody?.getMonth === 'function') {
    // is Date. Though arguably - if there's any functions at all on the object, then we can probably assume that
    // it's NOT a CRField, nor a CRObject
    return currentBody;
  } else if (currentBody) {
    const mergedObject = {} as any;

    for (const [key, child] of Object.entries(currentBody)) {
      if (
        child === null ||
        child === undefined ||
        key === 'scene' ||
        key === 'multiScene'
      ) {
        // null, undefined or a scene/scene array. Don't merge scenes
        mergedObject[key] = child;
      } else if (Array.isArray(child)) {
        if (child.length > 0 && typeof child[0] === 'object') {
          const lastValuesChild = lastBody && (lastBody[key] as any[]);
          // All children within an array, in order to be matched, must either have an identifier, or is a field
          mergedObject[key] = child.map(arrayChild =>
            recurseBuildCRFields(
              arrayChild,
              lastValuesChild?.find(({ fieldName, identifier }: any) =>
                isCRField(arrayChild)
                  ? fieldName === arrayChild.fieldName
                  : identifier !== undefined &&
                    identifier === arrayChild.identifier,
              ),
              onCRField,
              onCRObject,
            ),
          );
        } else {
          mergedObject[key] = child;
        }
      } else if (typeof child === 'object') {
        const lastValuesChild = lastBody && lastBody[key];
        mergedObject[key] = recurseBuildCRFields(
          child,
          lastValuesChild,
          onCRField,
        );
      } else {
        // primitive
        mergedObject[key] = child;
      }
    }
    if (onCRObject) {
      return onCRObject(mergedObject, currentBody, lastBody);
    } else {
      return mergedObject;
    }
  }
};
export const applyChangeToBody = (
  body: any,
  fieldName: string,
  value: any,
): [any, CRField | undefined] => {
  let resultField = undefined;
  const newBody = recurseBuildCRFields(body, undefined, field => {
    if (field.fieldName === fieldName) {
      resultField = {
        ...field,
        client: {
          value,
          sequenceNum: (field.client.sequenceNum ?? 1) + 1,
          validationResult: validate(value, field),
        },
      };
      return resultField;
    } else if (
      field.fieldType === FieldType.compound &&
      fieldName.startsWith(field.fieldName + '/')
    ) {
      let appendValueToCompoundValue = true;
      const newSequenceNum = (field.client.sequenceNum ?? 1) + 1;
      const compoundValue = {} as Record<string, any>;
      const subFields = field.fields.map(subField => {
        const subFieldValue =
          subField.fieldName === fieldName ? value : subField.client.value;
        if (appendValueToCompoundValue) {
          compoundValue[stripFieldName(subField.fieldName)] = subFieldValue;
        }
        if (subField.fieldName === fieldName) {
          appendValueToCompoundValue =
            !subField.resetSubsequentQuestionsOnNewValue;
        }
        return {
          ...subField,
          client: {
            value: subFieldValue,
            // Always revalidate. For fields that aren't the field that got modified, it will clear CRWarnings, thus
            // allowing the update to proceed
            validationResult: validate(subFieldValue, subField),
            sequenceNum: newSequenceNum,
          },
        };
      });

      resultField = {
        ...field,
        client: {
          value: compoundValue,
          sequenceNum: (field.client.sequenceNum ?? 1) + 1,
          validationResult: subFields.find(
            subField => subField.client.validationResult,
          )?.client.validationResult,
        },
        fields: subFields,
      };
      return resultField;
    } else {
      return field;
    }
  });
  return [newBody, resultField];
};

// SS 20231020 Note the below will conflict with V3780, V3780's version should be taken
export const findFieldsWithClientValidationErrors = (body: any): CRField[] => {
  const result: CRField[] = [];
  recurseBuildCRFields(body, undefined, field => {
    if (
      field.client.validationResult &&
      (SBMountedFields[field.fieldName] ?? 0) > 0
    ) {
      result.push(field);
      return field;
    }
  });
  return result;
};
